import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import media from 'styled-media-query'
import Modal from '../../organisms/CenterCards/Modal'
import Button from '../../atoms/Button/ButtonV2'
import getID from '../../../utils/getCloudinaryID'
import iconPhoneWhite from '../../../images/icon-phone-white.svg'
import StarRatings from '../../StarRatings/StarRatings'
import { createDataLayerIfNotExists } from '../../../utils/global/createDataLayerIfNotExists'
import useSegment from '../../../hooks/segment/useSegment'
import { getCenterProfileSlug, getPhoneNumbers } from '../../../domain/center'
import { scrollToHref } from '../../../utils/scrollTo'

const OBSERVER_TRIGGER_THRESHOLD = '-100px'

const EmailUsNavbar = styled.div`
  :target {
    display: block;
    top: -120px;
    visibility: hidden;
  }

  align-items: center;
  background-color: white;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  z-index: 100;

  ${media.greaterThan('medium')`
    gap: 16px;
    padding: 8px 16px;
  `}

  ${media.greaterThan('large')`
    gap: 24px;
    padding: 8px 24px;
  `}

  .thumbnail {
    flex-shrink: 0;

    img {
      border-radius: 100%;
      height: 32px;
      object-fit: cover;
      width: 32px;

      ${media.greaterThan('medium')`
        height: 40px;
        width: 40px;
      `}

      ${media.greaterThan('large')`
        height: 64px;
        width: 64px;
      `}
    }
  }

  .title {
    flex: 1;
    min-width: 0;

    > * + * {
      margin-top: 4px;
    }

    .center-title {
      font-size: 16px;
      overflow: hidden;
      text-align: left;
      text-overflow: ellipsis;
      white-space: nowrap;

      ${media.greaterThan('medium')`
        font-size: 18px;
      `}

      ${media.greaterThan('large')`
        font-size: 24px;
      `}
    }

    .center-location {
      display: none;
      font-size: 14px;
      text-align: left;

      ${media.greaterThan('medium')`
        display: block;
      `}

      ${media.greaterThan('large')`
        font-size: 16px;
      `}
    }
  }

  .cta {
    flex-shrink: 0;

    a {
      display: block;
      align-self: center;
      img {
        height: 25px;
        vertical-align: middle;
      }
    }
    p {
      vertical-align: 'center';
    }
    .flex-group {
      display: flex;
      gap: 30px;
      ${media.lessThan('medium')`
      display: none;

      `}
    }
  }
`

const OverviewNavbar = styled.div`
  width: 100vw;
  height: 37px;
  background: #f7f7f7;
  overflow-x: auto;

  .nav-container {
    display: flex;
    height: 100%;
    margin-right: 20px;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    color: #162941;

    a {
      text-decoration: none;
      flex: none;
      padding-right: 8px;
      padding-left: 8px;
      font-style: normal;
      font-weight: 400;
      font-size: 14px !important;
      color: #162941;
      font-size: 1.1rem;
      align-self: center;
      border: 4px solid transparent;
      line-height: 35px;
      margin-bottom: 4px;
      opacity: 0.8;
      cursor: pointer;
      &:hover {
        opacity: 1;
      }
      a {
        height: 100%;
        width: 100%;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
        line-height: 16px;
        color: #162941;
        &:target {
          display: block;
          position: relative;
          top: -120px;
          visibility: hidden;
        }
      }
      &.active {
        border: 0px solid transparent;
        border-bottom: 2px solid #31279b;
        color: #31279b;
        box-sizing: border-box;
        margin-bottom: 0px;
        opacity: 1;
        font-style: normal;
        font-weight: 400;
        font-size: 14px;
      }
    }
  }
  &.active {
    border: 0px solid transparent;
    border-bottom: 2px solid #31279b;
    color: #31279b;
    box-sizing: border-box;
    margin-bottom: 0px;
    opacity: 1;
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
  }
`

const OverviewNavbarInner = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1200px;
  margin: 0 auto;
  height: 100%;
`

const StickyBar = ({
  data,
  analyticsDashboard,
  queryID,
  navigationLinks,
  showEmailNavbar,
  reviewsCount,
  reviewsAverage
}) => {
  const segment = useSegment()

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [visibleSectionId, setVisibleSectionId] = useState(
    navigationLinks?.[0]?.sectionId
  )

  const toggleModal = () => {
    setIsModalVisible(!isModalVisible)
    document.querySelector('html').style.overflow = isModalVisible
      ? 'auto'
      : 'hidden'
  }
  useEffect(() => {
    const sectionsInViewport = {
      [visibleSectionId]: true
    }

    const observer = new IntersectionObserver(
      (entries) => {
        // When some section enters or leaves the viewport, update the map of visible sections
        for (const entry of entries) {
          sectionsInViewport[entry.target.id] = entry.isIntersecting
        }

        // We’re using navigationLinks.filter() and not just Object.keys(sectionsInViewport)
        // because we want to keep the order
        const allVisibleSections = navigationLinks.filter((link) => {
          return sectionsInViewport[link.sectionId]
        })
        const topmostVisibleSection = allVisibleSections[0]
        // Sometimes, there might be no visible section. This happens if we have the following DOM structure:
        // <section id="section1">
        // <section>
        // <section>
        // <section>
        // <section id="section2">
        // and the user scrolls past section1 but not yet to section2.
        // (In this case, we keep the last active section as is.)
        if (topmostVisibleSection) {
          setVisibleSectionId(topmostVisibleSection.sectionId)
        }
      },
      {
        // Trigger the callback when the top of the section is X pixels above or below the viewport
        // This is to offset the sticky bar
        rootMargin: `${OBSERVER_TRIGGER_THRESHOLD} 0px ${OBSERVER_TRIGGER_THRESHOLD} 0px`
      }
    )

    const allSections = navigationLinks
      .map((link) => document.getElementById(link.sectionId))
      .filter((section) => section !== null)
    for (const element of allSections) {
      observer.observe(element)
    }

    return () => {
      observer.disconnect()
    }
  }, [])

  // the below code pertains to Google Tag Manager and may have no context outside of this file; DO NOT DELETE.
  const handleDataLayerStickyBarPhoneNumberClick = () => {
    segment.ui.callCta({
      ...getPhoneNumbers(data)[0],
      center_slug: getCenterProfileSlug(data)
    })
    createDataLayerIfNotExists()
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'purchase',
      ecommerce: {
        items: [
          {
            item_name: data.title,
            item_id: data.slug,
            item_variant: data.listing_template,
            item_category: 'Sticky Bar Phone Number'
          }
        ]
      }
    })
  }

  const handleDataLayerInquiryClick = () => {
    createDataLayerIfNotExists()
    window.dataLayer.push({ ecommerce: null })
    window.dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        items: [
          {
            item_name: data.title,
            item_id: data.slug,
            item_variant: data.listing_template,
            item_category: 'Inquiry Form Modal Click'
          }
        ]
      }
    })
  }

  const centerHasReviews = reviewsCount > 0

  return (
    <>
      {isModalVisible && (
        <Modal
          queryID={queryID}
          dashboard={analyticsDashboard || ''}
          busContact={data.business_contact || ''}
          slug={data.slug}
          listing_template={data.listing_template}
          email={data.email}
          toggle={toggleModal}
          title={data.title}
          trackingID={'Sticky Bar'}
          location={data.location_label}
          image={data.slides[0]}
          setModal={setIsModalVisible}
          insuranceAccepted={
            !!(
              data?.amenities?.includes('Insurance Accepted') ||
              data?.price?.includes('Insurance Accepted')
            )
          }
        />
      )}
      <div
        id="sticky-bar-container"
        className="sticky top-0 z-30 w-full overflow-hidden"
      >
        <EmailUsNavbar
          id="profile-sticky-bar"
          //This is a small 'hack'. The transition get triggered with the last duration that is set.
          //So, 300ms is for when we animate the sticky bar to show and we set the duration for the transition to hide it.
          className={`transition-all duration-300 ease-linear ${
            showEmailNavbar
              ? 'mt-0'
              : `${
                  centerHasReviews
                    ? 'mt-[-75px] md:mt-[-102px] lg:mt-[-113px]'
                    : 'mt-[-75px] md:mt-[-102px] lg:mt-[-80px]'
                } duration-150`
          }`}
        >
          <div className="thumbnail">
            {data && data.featured_image && (
              <img
                src={`https://res.cloudinary.com/rehabpath/image/upload/c_limit,w_160,h_160,f_auto/${getID(
                  data.featured_image
                )}`}
                alt=""
              />
            )}
          </div>
          <h3 className="title">
            <div className="center-title font-semibold text-gray-1100">
              {data.title}
            </div>
            <div className="center-location font-medium text-gray-800">
              {data.location_label}
            </div>
            {centerHasReviews && (
              <a
                className="flex cursor-pointer items-center gap-2"
                href="#profile-reviews"
              >
                {reviewsAverage ? (
                  <div className="mt-px inline-block">
                    <StarRatings
                      rating={Number(reviewsAverage.toString().substring(0, 3))}
                      starRatedColor="#FFC130"
                      starSpacing="2px"
                      numberOfStars={5}
                      uniqueKey="sticky-bar"
                      starDimension="20px"
                    />
                  </div>
                ) : (
                  ''
                )}{' '}
                <span className="mt-[5px] font-medium text-gray-800">
                  ({reviewsCount || '0'} Reviews)
                </span>
              </a>
            )}
          </h3>
          <div className="cta">
            <div className="flex-group">
              {data.primary_cta === 'message' ||
              data.primary_cta === 'email' ? (
                <>
                  <Button
                    onClick={() => {
                      toggleModal()
                      handleDataLayerInquiryClick()
                    }}
                    as="button"
                  >
                    Message Us
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleDataLayerStickyBarPhoneNumberClick}
                    as="a"
                    href={`tel: ${data.phone_numbers?.[0]?.number}`}
                    id="phone-number-click"
                  >
                    <div className="flex items-center gap-2">
                      <img
                        src={iconPhoneWhite}
                        className="w-4 text-white"
                      />
                      <span className="mt-px">
                        {data.phone_numbers?.[0]?.number}
                      </span>
                    </div>
                  </Button>
                </>
              )}
            </div>
          </div>
        </EmailUsNavbar>
        <OverviewNavbar>
          <OverviewNavbarInner>
            <div
              style={{ overflowY: 'hidden' }}
              className="nav-container hide-scroll"
            >
              {navigationLinks.map((item) => {
                const className =
                  visibleSectionId === item.sectionId ? 'active' : ''
                return (
                  <a
                    className={className}
                    href={`#${item.sectionId}`}
                    onClick={(event) => {
                      event.preventDefault()

                      const stickyHeaderHeightOffset =
                        document?.getElementById('sticky-bar-container')
                          ?.offsetHeight || 0

                      scrollToHref({
                        element: event.currentTarget,
                        offset: stickyHeaderHeightOffset * -1
                      })
                    }}
                    key={item.label}
                  >
                    {item.label}
                  </a>
                )
              })}
            </div>
          </OverviewNavbarInner>
        </OverviewNavbar>
      </div>
    </>
  )
}

export default StickyBar
